import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import type { GridPaginationModel, GridRowParams } from '@mui/x-data-grid-pro'
import { EmptyTableMessage } from 'src/components/payments-ops/EmptyTableMessage'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { PAYMENTS_OPS_PAGE_SIZE_OPTIONS } from 'src/constants/payments-ops/constants'
import { buildFeeWithdrawalColumns } from 'src/constants/payments-ops/feeWithdrawalsGrid'
import type { FeeWithdrawal } from 'src/interfaces/feeWithdrawal'
import {
  getFeeWithdrawals,
  getFeeWithdrawalsLoading,
  getFeeWithdrawalsPagination,
} from 'src/selectors/feeWithdrawals'
import { filterFeeWithdrawalsByReport } from 'src/utils/paymentsOps/feeWithdrawals'

interface Props {
  hasReportOnly: boolean
  paginationModel: GridPaginationModel
  onPaginationModelChange(model: GridPaginationModel): void
  onRowClick(withdrawal: FeeWithdrawal): void
  onDownloadReport(withdrawal: FeeWithdrawal): void
}

const getRowId = (row: FeeWithdrawal): string => {
  return row.id
}

const renderEmpty = () => {
  return <EmptyTableMessage message="No withdrawals found" />
}

const FeeWithdrawalsTableComponent = ({
  hasReportOnly,
  paginationModel,
  onPaginationModelChange,
  onRowClick,
  onDownloadReport,
}: Props) => {
  const withdrawals = useSelector(getFeeWithdrawals)
  const pagination = useSelector(getFeeWithdrawalsPagination)
  const isLoading = useSelector(getFeeWithdrawalsLoading)

  const rows = useMemo(() => {
    return filterFeeWithdrawalsByReport(withdrawals, hasReportOnly)
  }, [hasReportOnly, withdrawals])

  const columns = useMemo(() => {
    return buildFeeWithdrawalColumns({ onDownloadReport })
  }, [onDownloadReport])

  const handleRowClick = useCallback(
    (params: GridRowParams<FeeWithdrawal>) => {
      onRowClick(params.row)
    },
    [onRowClick],
  )

  return (
    <GridTable<FeeWithdrawal>
      rows={rows}
      columns={columns}
      getRowId={getRowId}
      isLoading={isLoading}
      fillHeight
      paginationMode="server"
      // The server total: "Has report" narrows the loaded page only, not the paging.
      rowCount={pagination.total ?? 0}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      pageSizeOptions={PAYMENTS_OPS_PAGE_SIZE_OPTIONS}
      sortingMode="server"
      EmptyElement={renderEmpty}
      className={tw`[& .MuiDataGrid-row]:cursor-pointer`}
      onRowClick={handleRowClick}
      tableDataTestId="fee-withdrawals-table"
      rowDataTestIdPrefix="fee-withdrawals"
    />
  )
}

export const FeeWithdrawalsTable = memo(FeeWithdrawalsTableComponent)
